import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Bell, ArrowUpRight, CalendarClock, AlertTriangle, CheckCircle2, Clock, Users,
} from "lucide-react";
import { HospitalHeader, BrandedShell, BrandedNav, StatChip } from "@/components/BrandedLayout";
import { FooterDisclaimer } from "@/components/FooterDisclaimer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { RISK_STYLES, timeAgo } from "@/lib/appUtils";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TIER_TO_STYLE = { Routine: "low", "Needs Attention": "moderate", "High-Risk": "high" };

export default function NurseDashboard() {
  const { hospCode } = useBranding();
  const [queue, setQueue] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => setQueue(await api.nurseQueue(hospCode));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hospCode]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return !q ? queue : queue.filter((p) => p.name.toLowerCase().includes(q) || p.surgery_type.toLowerCase().includes(q));
  }, [queue, filter]);

  const dueToday = filtered.filter((p) => p.next_call_day === p.recovery_day).length;
  const overdue = filtered.reduce((n, p) => n + (p.overdue_calls?.length || 0), 0);
  const highRisk = filtered.filter((p) => p.tier === "High-Risk").length;

  const logCall = (p) => toast.success(`Call logged for ${p.name}`, { description: p.contact_phone || "Contact on file" });

  return (
    <BrandedShell>
      <HospitalHeader subtitle="Nurse dashboard · follow-up queue" testId="nurse-header" />
      <BrandedNav role="doctor" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatChip icon={Users} label="In queue" value={filtered.length} testId="nurse-stat-queue" accent />
        <StatChip icon={CalendarClock} label="Calls due today" value={dueToday} testId="nurse-stat-due-today" accent />
        <StatChip icon={Clock} label="Overdue calls" value={overdue} testId="nurse-stat-overdue" />
        <StatChip icon={AlertTriangle} label="High-risk" value={highRisk} testId="nurse-stat-high-risk" />
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="text-sm text-muted-foreground">
          Patients under nurse care: <span className="font-medium text-foreground">Needs Attention</span> tier + surgeon-flagged.
        </div>
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or surgery"
          data-testid="nurse-filter-input"
          className="h-8 text-sm max-w-xs"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="nurse-queue">
        {filtered.map((p) => {
          const style = RISK_STYLES[TIER_TO_STYLE[p.tier]];
          const dueNow = p.next_call_day === p.recovery_day;
          return (
            <div
              key={p.id}
              data-testid={`nurse-row-${p.id}`}
              className={cn(
                "surface-card p-5 shadow-sm flex flex-col gap-3",
                p.tier === "High-Risk" && "border-rose-300/60 dark:border-rose-800"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={cn("inline-flex items-center text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border", style.badge)}>
                      {p.tier}
                    </span>
                    {p.nurse_assistance_enabled && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">
                        <Phone className="w-3 h-3" /> Surgeon-flagged
                      </span>
                    )}
                    {dueNow && (
                      <span data-testid={`nurse-due-now-${p.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                        <CalendarClock className="w-3 h-3" /> Due today
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-foreground truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.surgery_type} · Day {p.recovery_day}/{p.care_plan_duration} · Last check-in {timeAgo(p.last_checkin)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-bold tabular-nums text-foreground leading-none">{p.risk_score}</div>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-medium mt-1">risk</div>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap text-xs">
                <div className="text-muted-foreground">
                  Next call: <span className="font-medium text-foreground">
                    {p.next_call_day ? `Day ${p.next_call_day}` : "—"}
                  </span>
                </div>
                {p.overdue_calls?.length > 0 && (
                  <div className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium">
                    <Clock className="w-3 h-3" /> {p.overdue_calls.length} overdue
                  </div>
                )}
                {p.upcoming_calls?.length > 0 && (
                  <div className="text-muted-foreground">
                    Upcoming: {p.upcoming_calls.slice(0, 3).map((d) => `D${d}`).join(", ")}
                    {p.upcoming_calls.length > 3 ? `+${p.upcoming_calls.length - 3}` : ""}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-border">
                <Button variant="outline" size="sm" className="rounded-full text-xs h-8"
                  data-testid={`nurse-call-btn-${p.id}`}
                  onClick={() => toast.success(`Calling ${p.name}…`, { description: p.contact_phone })}>
                  <Phone className="w-3 h-3 mr-1.5" /> Call
                </Button>
                <Button variant="outline" size="sm" className="rounded-full text-xs h-8"
                  data-testid={`nurse-remind-btn-${p.id}`}
                  onClick={() => toast.success(`Reminder queued for ${p.name}`)}>
                  <Bell className="w-3 h-3 mr-1.5" /> Remind
                </Button>
                <Button variant="outline" size="sm" className="rounded-full text-xs h-8"
                  data-testid={`nurse-log-btn-${p.id}`}
                  onClick={() => logCall(p)}>
                  <CheckCircle2 className="w-3 h-3 mr-1.5" /> Log call
                </Button>
                <Button asChild size="sm" className="rounded-full text-xs h-8 ml-auto"
                  data-testid={`nurse-view-btn-${p.id}`}>
                  <Link to={`/${hospCode}/doctor/patient/${p.id}`}>
                    Open protocol <ArrowUpRight className="w-3 h-3 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="lg:col-span-2 surface-card p-10 text-center text-muted-foreground">
            Nurse queue is clear. No patients need follow-up right now.
          </div>
        )}
      </div>

      <FooterDisclaimer />
    </BrandedShell>
  );
}
